import React, { useState } from 'react';
import { Mail, MessageSquare, Send } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { submitContact } from '../lib/api';
import { toast } from 'sonner';

const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await submitContact(formData);
            toast.success('Message sent successfully! We\'ll get back to you soon.');
            setFormData({ name: '', email: '', subject: '', message: '' });
        } catch (error) {
            toast.error('Failed to send message. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout>
            <div data-testid="contact-page">
                {/* Hero */}
                <section className="bg-navy text-white py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4">Contact Us</h1>
                        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                            Have questions? We're here to help. Reach out to our team.
                        </p>
                    </div>
                </section>

                {/* Contact Content */}
                <section className="py-16 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid lg:grid-cols-2 gap-12">
                            {/* Contact Info */}
                            <div>
                                <h2 className="font-heading text-2xl font-bold text-navy mb-6">Get in Touch</h2>
                                <p className="text-gray-600 mb-8">
                                    Whether you have a question about our platform, need help with your account, 
                                    or want to discuss advertising opportunities, our team is ready to help.
                                </p>

                                <div className="space-y-6">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <Mail className="w-6 h-6 text-gold" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-navy">Email</h3>
                                            <p className="text-gray-600">info@hotelmotelclub.com</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <MessageSquare className="w-6 h-6 text-gold" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-navy">Contact Form</h3>
                                            <p className="text-gray-600">Use the form to send us a message</p>
                                            <p className="text-sm text-gray-500">We typically respond within 24 hours</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Contact Form */}
                            <div className="bg-white rounded-xl shadow-sm p-6 sm:p-8">
                                <h2 className="font-heading text-2xl font-bold text-navy mb-6">Send a Message</h2>
                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div>
                                        <Label htmlFor="name">Your Name *</Label>
                                        <Input
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            className="mt-1"
                                            data-testid="name-input"
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="email">Email Address *</Label>
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            className="mt-1"
                                            data-testid="email-input"
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="subject">Subject *</Label>
                                        <Input
                                            id="subject"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            required
                                            className="mt-1"
                                            data-testid="subject-input"
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="message">Message *</Label>
                                        <Textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            rows={5}
                                            className="mt-1"
                                            data-testid="message-input"
                                        />
                                    </div>

                                    <Button
                                        type="submit"
                                        className="w-full bg-gold hover:bg-amber-600"
                                        disabled={loading}
                                        data-testid="submit-btn"
                                    >
                                        {loading ? 'Sending...' : (
                                            <>
                                                <Send className="w-4 h-4 mr-2" />
                                                Send Message
                                            </>
                                        )}
                                    </Button>
                                </form>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </Layout>
    );
};

export default ContactPage;
